import { AlertTriangle, Check, CircleAlert, FileCheck2, ShieldCheck } from 'lucide-react';
import type { ProductConfig, ProductViewId } from '../domain/types';
import { DEFAULT_A4_PAGE_PADDING_MM, inspectA4SheetFit } from '../export/a4-sheet';
import { deriveTransferGroups } from '../export/transfer-groups';
import type { DesignStore } from '../store/design-store';
import { useDesignSnapshot } from '../store/react';
import { createValidationReport, validateDesign } from '../validation';
import { commands } from '../store/commands';
import { formatMm } from './OptionsStep';

export function ReviewStep({
  product,
  store,
  onEditIssue,
}: {
  product: ProductConfig;
  store: DesignStore;
  onEditIssue: (viewId: ProductViewId, objectId?: string) => void;
}) {
  const { state } = useDesignSnapshot();
  const report = createValidationReport(validateDesign(state.design, product));
  const { results: issues, errors } = report;
  const pendingWarnings = report.unacknowledgedWarnings;
  const transfers = deriveTransferGroups(state.design, product);
  const objectCount = transfers.reduce((total, transfer) => total + transfer.objectIds.length, 0);
  const workflow = product.productionRules.workflow;
  const a4PagePaddingMm = workflow?.pagePaddingMm ?? DEFAULT_A4_PAGE_PADDING_MM;

  return (
    <section className="step-content review-step">
      <div className="content-heading">
        <div>
          <p className="eyebrow">4 / 5 · Kontrola návrhu</p>
          <h2>Je návrh pripravený?</h2>
        </div>
        <p>
          Každý motív sa pripraví v skutočnom rozmere na A4. Ak ich je na jednej strane viac,
          zachová sa presná poloha každého z nich. Výrobný výkres, cenová ponuka a čistý ZIP pre
          tlač budú v zhrnutí oddelené.
        </p>
      </div>
      <div
        className={`readiness-banner ${errors.length ? 'error' : pendingWarnings.length ? 'warning' : 'ready'}`}
      >
        <span>
          {errors.length ? (
            <CircleAlert />
          ) : pendingWarnings.length ? (
            <AlertTriangle />
          ) : (
            <ShieldCheck />
          )}
        </span>
        <div>
          <h3>
            {errors.length
              ? `${errors.length} kritických problémov`
              : pendingWarnings.length
                ? 'Návrh potrebuje vašu pozornosť'
                : 'Návrh je pripravený na výrobu'}
          </h3>
          <p>
            {errors.length
              ? 'Pred pokračovaním opravte označené problémy.'
              : report.unacknowledgedWarnings.length
                ? 'Skontrolujte a potvrďte všetky upozornenia.'
                : 'Návrh spĺňa požiadavky na výrobu.'}
          </p>
        </div>
      </div>

      <div className="transfer-review-list" aria-label="Motívy v objednávke">
        {transfers.map(transfer => {
          const a4Fit = inspectA4SheetFit(transfer.artworkBoundsMm, a4PagePaddingMm);
          return (
            <article className="transfer-review-card" key={transfer.id}>
              <span className="transfer-index">{transfer.viewId === 'front' ? 'P' : 'Z'}</span>
              <div>
                <strong>{transfer.label}</strong>
                <small>
                  Samostatný A4 podklad · {transfer.objectIds.length}{' '}
                  {transfer.objectIds.length === 1 ? 'prvok' : 'prvky'} · {state.design.quantity}×
                </small>
              </div>
              <b className={a4Fit.fits ? 'a4-fit-ok' : 'a4-fit-error'}>
                {formatMm(transfer.artworkBoundsMm.width)} ×{' '}
                {formatMm(transfer.artworkBoundsMm.height)} mm
                <small>
                  {a4Fit.fits
                    ? `A4 ${formatA4Orientation(a4Fit.layout.orientation)} ✓`
                    : 'Nevojde na A4'}
                </small>
              </b>
            </article>
          );
        })}
        {!transfers.length && (
          <article className="transfer-review-card empty">
            <CircleAlert />
            <div>
              <strong>Zatiaľ bez potlače</strong>
              <small>Vráťte sa do editora a pridajte obrázok, text, tvar alebo kresbu.</small>
            </div>
          </article>
        )}
      </div>

      <div className="review-grid">
        <div className="issues-card">
          <div className="card-heading">
            <h3>Výsledky kontroly</h3>
            <span>{issues.length} položiek</span>
          </div>
          <div className="issue-list">
            {issues.map(issue => (
              <div
                key={issue.id}
                className={`issue ${issue.severity} ${issue.acknowledged ? 'acknowledged' : ''}`}
                onClick={() => {
                  if (issue.viewId) onEditIssue(issue.viewId, issue.objectId);
                }}
              >
                <span>{issue.severity === 'error' ? <CircleAlert /> : <AlertTriangle />}</span>
                <div>
                  <strong>{issue.message}</strong>
                  <small>
                    {issue.viewId ? viewName(issue.viewId) : 'Celý návrh'}
                    {issue.objectId ? ' · kliknutím otvoríte objekt v editore' : ''}
                  </small>
                  {issue.severity === 'warning' && (
                    <button
                      type="button"
                      onClick={event => {
                        event.stopPropagation();
                        store.dispatch(
                          commands.acknowledgeWarning(
                            store.getState(),
                            issue.id,
                            !issue.acknowledged,
                          ),
                        );
                      }}
                    >
                      {issue.acknowledged ? 'Potvrdené ✓' : 'Potvrdiť upozornenie'}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {issues.length === 0 && (
              <div className="all-clear">
                <span>
                  <Check />
                </span>
                <h3>Všetko v poriadku</h3>
                <p>V rozsahu dostupných pravidiel sme nenašli chyby ani upozornenia.</p>
              </div>
            )}
          </div>
        </div>

        <aside className="production-card">
          <div className="card-heading">
            <h3>Výrobné podklady</h3>
            <FileCheck2 />
          </div>
          <dl>
            <div>
              <dt>Počet tričiek</dt>
              <dd>{state.design.quantity} ks</dd>
            </div>
            <div>
              <dt>Motívy / tričko</dt>
              <dd>{transfers.length}</dd>
            </div>
            <div>
              <dt>Podklady spolu</dt>
              <dd>
                {transfers.length} {transfers.length === 1 ? 'motív' : 'motívy'} ×{' '}
                {formatCopyCount(state.design.quantity)}
              </dd>
            </div>
            <div>
              <dt>Prvky spolu</dt>
              <dd>{objectCount}</dd>
            </div>
          </dl>
          <div className="export-options">
            <label>
              Po úspešnej kontrole
              <strong>Každý výstup stiahnete v zhrnutí samostatne</strong>
            </label>
          </div>
          <p className="review-output-note">
            Výrobný výkres, cenová ponuka a čistý ZIP pre tlač sú oddelené, aby sa dokumenty
            nemiešali so súbormi pre stroj.
          </p>
        </aside>
      </div>
    </section>
  );
}

function viewName(view: string) {
  return (
    (
      {
        front: 'Predná strana',
        back: 'Zadná strana',
        'left-sleeve': 'Ľavý rukáv',
        'right-sleeve': 'Pravý rukáv',
      } as Record<string, string>
    )[view] ?? view
  );
}

function formatCopyCount(quantity: number): string {
  if (quantity === 1) return '1 kópia';
  if (quantity >= 2 && quantity <= 4) return `${quantity} kópie`;
  return `${quantity} kópií`;
}

function formatA4Orientation(orientation: 'portrait' | 'landscape'): string {
  return orientation === 'portrait' ? 'na výšku' : 'na šírku';
}
